import { PHONE_DISPLAY, PHONE_TEL, EMAIL, BUSINESS_NAME, SERVICES, TID } from "@/config/site";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

export default function Footer() {
  const year = new Date().getFullYear();
  const ref = useReveal();
  return (
    <footer className="relative bg-black text-zinc-300 border-t border-yellow-400/30 overflow-hidden">
      {/* Ambient bottom glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] opacity-30"
        style={{ background: "radial-gradient(ellipse at center, rgba(250,204,21,0.15) 0%, rgba(0,0,0,0) 65%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div ref={ref} className="lux-reveal relative max-w-7xl mx-auto px-4 sm:px-6 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 lux-glass rounded-lg p-1.5 flex items-center justify-center">
              <img src="/images/logo.png" alt="BM Towing & Recovery logo" width="48" height="48" decoding="async" loading="lazy" className="max-h-full max-w-full object-contain" />
            </div>
            <div>
              <div className="font-display text-2xl font-black uppercase text-white leading-none tracking-tight">{BUSINESS_NAME}</div>
              <div className="font-display text-[11px] tracking-[0.28em] text-yellow-400 uppercase mt-1">Kirkland, WA · 24/7</div>
            </div>
          </div>
          <p className="mt-6 text-sm text-zinc-400 leading-relaxed font-light">
            Family-run towing and roadside assistance serving Kirkland, Bellevue, Redmond, Bothell, Woodinville, Sammamish, Issaquah, Mercer Island, Newcastle and the surrounding Eastside.
          </p>
          <a
            href={`tel:${PHONE_TEL}`}
            className="mt-6 inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-5 py-3 rounded-full lux-glow-yellow transition-colors text-sm"
          >
            <Phone className="h-4 w-4" strokeWidth={3} /> {PHONE_DISPLAY}
          </a>
        </div>

        <div>
          <div className="font-display uppercase tracking-[0.28em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-4 bg-yellow-400 rounded-full" />
            Services
          </div>
          <ul className="mt-5 space-y-2.5 text-sm font-light">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <a href={`/${s.slug}`} className="text-zinc-400 hover:text-yellow-400 transition-colors">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="font-display uppercase tracking-[0.28em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-4 bg-yellow-400 rounded-full" />
            Cities We Serve
          </div>
          <ul className="mt-5 space-y-2.5 text-sm font-light">
            {[
              ["bellevue", "Tow Truck in Bellevue", "/tow-truck-bellevue"],
              ["kirkland", "24/7 Towing Service in Kirkland", "/kirkland-towing"],
              ["redmond", "Tow Truck in Redmond", "/tow-truck-redmond"],
              ["bothell", "Tow Truck in Bothell", "/tow-truck-bothell"],
              ["woodinville", "Tow Truck in Woodinville", "/tow-truck-woodinville"],
              ["sammamish", "Tow Truck in Sammamish", "/tow-truck-sammamish"],
              ["issaquah", "Tow Truck in Issaquah", "/tow-truck-issaquah"],
              ["mercer-island", "Tow Truck in Mercer Island", "/tow-truck-mercer-island"],
              ["newcastle", "Tow Truck in Newcastle", "/tow-truck-newcastle"],
            ].map(([slug, label, href]) => (
              <li key={slug}>
                <a href={href} className="text-zinc-400 hover:text-yellow-400 transition-colors">
                  {label}
                </a>
              </li>
            ))}
            <li className="pt-1">
              <a href="/cities" className="text-yellow-400 hover:text-yellow-300 font-display uppercase tracking-widest text-xs transition-colors">
                View all cities →
              </a>
            </li>
          </ul>
        </div>

        <div>
          <div className="font-display uppercase tracking-[0.28em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-4 bg-yellow-400 rounded-full" />
            Contact
          </div>
          <ul className="mt-5 space-y-4 text-sm font-light">
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-yellow-400 shrink-0 mt-1" strokeWidth={2} />
              <a href={`tel:${PHONE_TEL}`} data-testid={TID.footerCall} className="text-white hover:text-yellow-400 font-display font-bold text-base transition-colors">
                {PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-yellow-400 shrink-0 mt-1" strokeWidth={2} />
              <a href={`mailto:${EMAIL}`} className="text-zinc-400 hover:text-yellow-400 break-all transition-colors">
                {EMAIL}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-yellow-400 shrink-0 mt-1" strokeWidth={2} />
              <span className="text-zinc-400">Kirkland, WA · Eastside &amp; Greater Seattle</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-4 w-4 text-yellow-400 shrink-0 mt-1" strokeWidth={2} />
              <span className="text-zinc-400">Open 24 hours · 7 days a week</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500 font-light">
          <div>© {year} {BUSINESS_NAME}. All rights reserved.</div>
          <div className="font-display uppercase tracking-[0.28em]">
            24/7 Emergency Line · <a href={`tel:${PHONE_TEL}`} className="text-yellow-400 hover:text-yellow-300 transition-colors">{PHONE_DISPLAY}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
